import type { FC, ReactNode } from "react";
import { useCallback } from "react";

import { useLocatorTranslations } from "@locators/components/shared/hooks/useLocatorTranslations";
import { useStoreLocatorContext } from "@locators/components/shared/hooks/useStoreLocatorContext";
import classNames from "classnames";

import { SearchDefaultIcon } from "@jti/brand-icons/search-default";
import { UiTargetIcon } from "@jti/brand-icons/ui-target";

import type { MobileDisplayType } from "../../types";

import styles from "./StoreLocatorSearchBarV2.module.scss";

type StoreLocatorSearchBarProps = {
  onChangeResultType?: (type: MobileDisplayType) => void;
  filtersSlot?: ReactNode;
  isFilterSlotAutoWidth?: boolean;
  isSearchInputFullWidth?: boolean;
};

export const StoreLocatorSearchBarV2: FC<StoreLocatorSearchBarProps> = ({
  filtersSlot,
  isFilterSlotAutoWidth = false,
  isSearchInputFullWidth = false,
}) => {
  const { __ } = useLocatorTranslations();
  const {
    inputRef,
    filtersEnabled,
    isSearchVisible,
    isInputExtraTextVisible,
    getUserMarkerPosition,
    searchAdditionalTitle,
    newFiltersNumber = 0,
    isOpenNowFilterEnabled,
    isProductColorsFilterEnabled,
    isProductFlavoursFilterEnabled,
    isProductTypesFilterEnabled,
    isStoreServicesFilterEnabled,
    isStoreTypeFilterEnabled,
  } = useStoreLocatorContext();

  const handleCurrentLocationClick = useCallback(() => {
    if (inputRef.current?.value) {
      inputRef.current.value = "";
    }
    getUserMarkerPosition();
  }, [getUserMarkerPosition, inputRef]);

  const isOnlyOpenNowFilterEnabled =
    isOpenNowFilterEnabled &&
    !isProductColorsFilterEnabled &&
    !isProductFlavoursFilterEnabled &&
    !isProductTypesFilterEnabled &&
    !isStoreServicesFilterEnabled &&
    !isStoreTypeFilterEnabled;

  return (
    <div
      role="search"
      data-testid="storeLocatorSearchBar"
      className={classNames(styles.barContainer, {
        [styles.horizontalAlignmentMobile]: !isSearchVisible,
        [styles.horizontalAlignment]: !isSearchVisible && (!filtersEnabled || isOnlyOpenNowFilterEnabled),
      })}
    >
      <div data-anchor="anchor-text">
        <h2 className={styles.barHeaderText} data-testid="PolicyPageInnerText">
          {__("storelocator.search_bar.header")}
        </h2>
        {isInputExtraTextVisible && (
          <div className={styles.inputExtraText} data-testid="inputExtraText">
            {__("storelocator.search_bar.input_extra_text")}
          </div>
        )}
        {!!searchAdditionalTitle && <h5>{searchAdditionalTitle}</h5>}
      </div>
      <div
        className={classNames(styles.contentContainer, {
          [styles.contentContainerFullWidth]: isFilterSlotAutoWidth || isSearchInputFullWidth,
        })}
      >
        {isSearchVisible && (
          <div
            className={classNames(styles.inputWrapper, {
              [styles.inputWrapperFullWidth]: isSearchInputFullWidth,
            })}
          >
            <div className={styles.searchInputLabel}>
              <SearchDefaultIcon className={classNames(styles.searchIcon, styles.searchDefaultIcon)} />
              <input
                ref={inputRef}
                id="storeSearchInput"
                className={classNames(styles.searchInput, "pac-target-input")}
                type="text"
                name="searchInput"
                placeholder={__("storelocator.placeholder.search")}
                aria-label={__("storelocator.placeholder.search")}
              />
              <button
                className={classNames([styles.currentLocationButton, styles.currentLocationButtonInput])}
                onClick={handleCurrentLocationClick}
                aria-label={__("storelocator.use_current_location")}
              >
                <UiTargetIcon className={styles.searchIcon} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
        {filtersEnabled && filtersSlot && (
          <div
            className={classNames(styles.filtersContainer, {
              [styles.withoutPaddings]: !isSearchVisible,
              [styles.noFilters]: newFiltersNumber === 0,
              [styles.oneItem]: newFiltersNumber === 1,
              [styles.filtersContainerAutoWidth]: isFilterSlotAutoWidth,
            })}
          >
            {filtersSlot}
          </div>
        )}
        {!isSearchVisible && (
          <button
            className={classNames(styles.currentLocationButton, {
              [styles.currentLocationButtonWhite]: !isSearchVisible,
            })}
            onClick={handleCurrentLocationClick}
            aria-label={__("storelocator.use_current_location")}
          >
            <UiTargetIcon height="20px" width="20px" aria-hidden="true" />
            <span>
              <span>{__("storelocator.use_current_location")}</span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
};
